import { ClubPlayer } from "@mixitone/models";
import { decodeJwt } from "@mixitone/util/supabase";
import { AuthChangeEvent, User } from "@supabase/supabase-js";
import { createSupabase } from "../supabaseClient";
import { PortalController } from "./PortalController";

interface State {
  authed: boolean;
  sessionChecked: boolean;
  authedByTag: boolean;
  loading: boolean;
  user?: User;
  clubPlayers: ClubPlayer[];
}

export class AuthController extends PortalController<State> {
  get initialState(): State {
    return {
      authed: false,
      sessionChecked: false,
      authedByTag: false,
      loading: true,
      clubPlayers: [],
    };
  }

  async initialize() {
    this.state.sessionChecked = localStorage.getItem("sessionChecked") === "true";

    this.supabase = createSupabase();
    this.supabase.auth.onAuthStateChange((event) => {
      this.handleAuthEvent(event);
    });
    this.observe(() => this.loadClubPlayers());
  }

  handleAuthEvent(event: AuthChangeEvent) {
    switch (event) {
      case "SIGNED_IN":
        this.checkSession();
        break;
      case "SIGNED_OUT":
        this.checkSession();
        break;
      case "INITIAL_SESSION":
        this.checkSession();
        break;
    }
  }

  async checkSession() {
    const response = await this.supabase.auth.getSession();
    if (response.error || !response.data.session?.user) {
      this.setState({ authed: false, sessionChecked: true, loading: false });
      return;
    }

    const session = response.data.session;
    const user = session.user;
    const jwt = decodeJwt(session.access_token);

    if (!this.state.sessionChecked) {
      localStorage.setItem("sessionChecked", "true");
    }

    this.setState({
      authed: true,
      sessionChecked: true,
      user,
      authedByTag: jwt.amr && jwt.amr.length === 1 && jwt.amr[0].method === "nfc",
    });
  }

  get emailIsVerified() {
    return this.state.authed && this.state.sessionChecked && !this.state.authedByTag;
  }

  async loadClubPlayers() {
    if (!this.state.authed) {
      this.state.clubPlayers = [];
      return;
    }

    const { data } = await this.supabase.auth.getSession();
    if (!data?.session?.user) {
      return;
    }

    const clubPlayers = await ClubPlayer.query()
      .eq("user_id", data.session.user.id)
      .preload("group", "club", "memberships")
      .all();
    this.state.clubPlayers = clubPlayers;
    this.state.loading = false;
  }

  async actionLogOut() {
    await this.supabase.auth.signOut();
    this.navigate("/");
  }
}
